import {
  LocationOn as LocationIcon,
  Search as SearchIcon,
  Store as StoreIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Rating,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchActiveSuppliers,
  searchSuppliers,
} from "../../../application/actions/supplierActions";
import {
  selectSuppliersData,
  selectSupplierSearchData,
  selectSupplierSearchTotalCount,
  selectSuppliersLoading,
} from "../../../application/selectors/suppliers";

const SupplierList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const suppliers = useSelector(selectSuppliersData);
  const loading = useSelector(selectSuppliersLoading);
  const searchResults = useSelector(selectSupplierSearchData);
  const totalCount = useSelector(selectSupplierSearchTotalCount);

  // Local State
  const [filters, setFilters] = useState({
    searchTerm: "",
    category: "",
    city: "",
    isVerified: "",
    sortBy: "createdAt",
    sortDescending: true,
  });

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 12,
  });

  useEffect(() => {
    loadSuppliers();
  }, [pagination.pageNumber]);

  const loadSuppliers = () => {
    dispatch(fetchActiveSuppliers(pagination));
  };

  const handleSearch = () => {
    const searchParams = {
      ...filters,
      ...pagination,
      pageNumber: 1,
    };
    dispatch(searchSuppliers(searchParams));
    setPagination({ ...pagination, pageNumber: 1 });
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  const handlePageChange = (event, value) => {
    setPagination({
      ...pagination,
      pageNumber: value,
    });
    window.scrollTo(0, 0);
  };

  const handleViewDetails = (supplierId) => {
    navigate(`/suppliers/${supplierId}`);
  };

  const handleClearFilters = () => {
    setFilters({
      searchTerm: "",
      category: "",
      city: "",
      isVerified: "",
      sortBy: "createdAt",
      sortDescending: true,
    });
    loadSuppliers();
  };

  const displaySuppliers =
    searchResults && searchResults.length > 0 ? searchResults : suppliers;

  const totalPages = Math.ceil(totalCount / pagination.pageSize) || 1;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          تأمین‌کنندگان
        </Typography>
        <Typography variant="body1" color="text.secondary">
          لیست تأمین‌کنندگان تجهیزات خورشیدی
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="جستجوی تأمین‌کننده..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange("searchTerm", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="شهر"
              value={filters.city}
              onChange={(e) => handleFilterChange("city", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>وضعیت تأیید</InputLabel>
              <Select
                value={filters.isVerified}
                label="وضعیت تأیید"
                onChange={(e) =>
                  handleFilterChange("isVerified", e.target.value)
                }
              >
                <MenuItem value="">همه</MenuItem>
                <MenuItem value="true">تأیید شده</MenuItem>
                <MenuItem value="false">تأیید نشده</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              جستجو
            </Button>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>مرتب‌سازی</InputLabel>
              <Select
                value={filters.sortBy}
                label="مرتب‌سازی"
                onChange={(e) => handleFilterChange("sortBy", e.target.value)}
              >
                <MenuItem value="createdAt">تاریخ ثبت</MenuItem>
                <MenuItem value="companyName">نام شرکت</MenuItem>
                <MenuItem value="rating">امتیاز</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <Button fullWidth variant="outlined" onClick={handleClearFilters}>
              پاک کردن فیلترها
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Suppliers Grid */}
      {!loading && (
        <>
          {displaySuppliers && displaySuppliers.length > 0 ? (
            <Grid container spacing={3}>
              {displaySuppliers.map((supplier) => (
                <Grid item xs={12} sm={6} md={4} key={supplier.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 4,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 60,
                            height: 60,
                            mr: 2,
                            bgcolor: "primary.main",
                          }}
                        >
                          <StoreIcon fontSize="large" />
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" noWrap>
                            {supplier.companyName}
                          </Typography>
                          {supplier.isVerified && (
                            <Chip
                              icon={<VerifiedIcon />}
                              label="تأیید شده"
                              size="small"
                              color="success"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </Box>
                      </Box>

                      {supplier.rating && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <Rating
                            value={supplier.rating}
                            readOnly
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            ({supplier.rating.toFixed(1)})
                          </Typography>
                        </Box>
                      )}

                      {supplier.city && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <LocationIcon
                            fontSize="small"
                            sx={{ mr: 0.5, color: "text.secondary" }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {supplier.city}
                          </Typography>
                        </Box>
                      )}

                      {supplier.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {supplier.description}
                        </Typography>
                      )}

                      <Box sx={{ mt: 2 }}>
                        <Chip
                          label={`${supplier.productsCount || 0} محصول`}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                        {supplier.category && (
                          <Chip
                            label={supplier.category}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleViewDetails(supplier.id)}
                        sx={{ mt: 2 }}
                      >
                        مشاهده محصولات
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary">
                تأمین‌کننده‌ای یافت نشد
              </Typography>
            </Paper>
          )}

          {/* Pagination */}
          {displaySuppliers && displaySuppliers.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={totalPages}
                page={pagination.pageNumber}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default SupplierList;
